import React, { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * EncounterErrorBoundary — scoped error boundary that wraps the
 * input/output encounter UI WITHOUT triggering the full-screen "App Error"
 * fallback. The whole point: if a fringe edge case crashes the output
 * panel mid-demo, the provider's typed/dictated input is preserved in
 * the parent App.js state and they can hit "Retry" without losing data.
 *
 * Recovers by:
 *   1. Resetting local error state on Retry click
 *   2. Dispatching `writeright:retry-generate` so App.js can re-run
 *      generation if the user wants
 *   3. NEVER touching localStorage (autosave lives there)
 */
class EncounterErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorCount: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console for browser DevTools + Sentry (if wired)
    // Keep the failure narrow — don't bubble up to the global ErrorBoundary
    console.error('Encounter UI crashed (scoped):', error, info?.componentStack);
    this.setState((prev) => ({ errorCount: prev.errorCount + 1 }));
    try {
      if (typeof window !== 'undefined') {
        const msg = error?.message || String(error);
        window.dispatchEvent(new CustomEvent('writeright:encounter-error', { detail: { message: msg } }));
      }
    } catch { /* ignore */ }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    try {
      window.dispatchEvent(new CustomEvent('writeright:retry-generate'));
    } catch { /* ignore */ }
  };

  handleReload = () => {
    // Only clear session state — NEVER touch localStorage (autosave lives there)
    try { sessionStorage.clear(); } catch { /* ignore */ }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const errMsg = this.state.error?.message || 'Unknown error';
      const isRecurring = this.state.errorCount >= 2;
      return (
        <div
          className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-5 my-3"
          data-testid="encounter-error-boundary-fallback"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-amber-300 mb-1">
                Something broke — your input is safe
              </h3>
              <p className="text-xs text-foreground/80 leading-relaxed mb-2">
                The encounter UI hit an unexpected error. Your typed notes, dictation,
                demographics, and autosaved drafts are still intact. Tap <strong>Retry</strong> to
                continue, or refresh the page if it keeps happening.
              </p>
              <p className="text-[10px] text-muted-foreground/80 font-mono break-all mb-3">
                {errMsg}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={this.handleRetry}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500/90 hover:bg-amber-500 text-amber-950 text-xs font-semibold transition-colors"
                  data-testid="encounter-error-retry"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retry
                </button>
                {isRecurring && (
                  <button
                    type="button"
                    onClick={this.handleReload}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-secondary hover:bg-secondary/80 text-foreground text-xs font-medium transition-colors"
                    data-testid="encounter-error-reload"
                  >
                    Refresh Page
                  </button>
                )}
              </div>
              {isRecurring && (
                <p className="text-[10px] text-amber-400/80 mt-2">
                  This is the {this.state.errorCount}rd time — consider refreshing or filing a Report
                  (wrench icon, bottom-right) so we can investigate.
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default EncounterErrorBoundary;
